// ============================================================================
// BOQ — library item short description, entered in five parts
// ============================================================================
// A version's short description is typed as five parts, in this fixed order:
//
//   Location; Type; Material; Thickness; Size
//
// and read everywhere else as one line built from them: the filled parts only,
// in that order, joined with "; " (so Location and Size alone read
// "Ground floor; 900 x 2100"). At least two parts must be filled.
//
// The parts live in their own columns on boq.item_versions (desc_location …
// desc_size). short_description is still the column every bill, export, search
// and snapshot reads; boq.compose_short_description() rewrites it from the
// parts on every insert and update, so the database is the one place the line
// is built. composeDescription() here is the same rule, used to preview the
// line as it is typed and to send it with the save.
//
// A version saved before the parts existed has none of them, and keeps its free
// text description until someone edits it; the edit then asks for the parts.
// ============================================================================

export const DESCRIPTION_PARTS = [
  { key: 'descLocation', column: 'desc_location', label: 'Location', placeholder: 'e.g. Ground floor' },
  { key: 'descType', column: 'desc_type', label: 'Type', placeholder: 'e.g. Flush door' },
  { key: 'descMaterial', column: 'desc_material', label: 'Material', placeholder: 'e.g. Teak veneer' },
  { key: 'descThickness', column: 'desc_thickness', label: 'Thickness', placeholder: 'e.g. 35 mm' },
  { key: 'descSize', column: 'desc_size', label: 'Size', placeholder: 'e.g. 900 x 2100' },
];

export const MIN_DESCRIPTION_PARTS = 2;
export const DESCRIPTION_SEPARATOR = '; ';

const clean = (v) => (v == null ? '' : String(v).trim());

// The one-line description the parts read as.
export function composeDescription(parts) {
  return DESCRIPTION_PARTS
    .map((p) => clean(parts?.[p.key]))
    .filter(Boolean)
    .join(DESCRIPTION_SEPARATOR);
}

export function countFilledParts(parts) {
  return DESCRIPTION_PARTS.filter((p) => clean(parts?.[p.key])).length;
}

// Whether a version was saved with parts, as opposed to a free text description
// from before they existed.
export function hasDescriptionParts(version) {
  return countFilledParts(version) > 0;
}

// Error message, or null when the parts are acceptable.
export function validateDescriptionParts(parts) {
  const n = countFilledParts(parts);
  if (n >= MIN_DESCRIPTION_PARTS) return null;
  const names = DESCRIPTION_PARTS.map((p) => p.label).join(', ');
  return n === 0
    ? `Short description: fill at least ${MIN_DESCRIPTION_PARTS} of ${names}.`
    : `Short description: fill at least one more of ${names} (${MIN_DESCRIPTION_PARTS} are needed).`;
}

// camelCase form fields → the five columns, trimmed, empty as null.
export function descriptionPartsPayload(parts) {
  const out = {};
  DESCRIPTION_PARTS.forEach((p) => { out[p.column] = clean(parts?.[p.key]) || null; });
  return out;
}

// A DB row (snake_case) → the five camelCase fields.
export function descriptionPartsFromRow(row) {
  const out = {};
  DESCRIPTION_PARTS.forEach((p) => { out[p.key] = row?.[p.column] ?? null; });
  return out;
}

// A version (camelCase) → form strings for the five boxes.
export function descriptionPartsForm(version) {
  const out = {};
  DESCRIPTION_PARTS.forEach((p) => { out[p.key] = version?.[p.key] || ''; });
  return out;
}

// The description fields a form opens with, from an existing version: its five
// parts, and, when it has none, its old free text as legacyDescription so the
// form can show it while it is split into the parts.
export function descriptionFormFields(version) {
  return {
    ...descriptionPartsForm(version),
    legacyDescription: version && !hasDescriptionParts(version) ? (version.shortDescription || '') : '',
  };
}

export const EMPTY_DESCRIPTION_FORM = descriptionFormFields(null);

export const DESCRIPTION_PART_COLUMNS = DESCRIPTION_PARTS.map((p) => p.column).join(', ');
